import { endOfQuarter, startOfQuarter } from 'date-fns';

// review cycle types for admin-growth module

export interface ReviewCycleApiData {
	id: string;
	company_id: string;
	notification_cadence: string;
	next_review_date: string | null;
	active: boolean;
	created_at: string;
	updated_at: string;
}

export interface ReviewCycle {
	id: string;
	companyId: string;
	notificationCadence: string;
	nextReviewDate: Date | null;
	active: boolean;
	createdAt: string;
	updatedAt: string;
}

/**
 * Available review cadence options for performance cycles
 * These are the only cadences that can be selected in the admin UI
 */
export const REVIEW_CADENCE_OPTIONS = ['quarterly', 'biannual', 'annual'] as const;

/**
 * Type for review cadence options
 */
export type ReviewCadenceOption = (typeof REVIEW_CADENCE_OPTIONS)[number];

/**
 * Formats a cadence string for display with proper capitalization and terminology
 * @param cadence - The raw cadence string (e.g., "quarterly", "biannual", "weekly")
 * @returns Formatted cadence string (e.g., "Quarterly", "Semiannual", "Weekly")
 */
export const formatReviewCadence = (cadence: string): string => {
	const lowerCadence = cadence.toLowerCase();

	switch (lowerCadence) {
		case 'biannual':
		case 'bi-annual':
		case 'semiannual':
		case 'semi-annual':
			return 'Semiannual';
		case 'quarterly':
			return 'Quarterly';
		case 'monthly':
			return 'Monthly';
		case 'biweekly':
		case 'bi-weekly':
			return 'Biweekly';
		case 'weekly':
			return 'Weekly';
		case 'annual':
		case 'annually':
			return 'Annual';
		default:
			// Capitalize first letter for unknown cadences
			return cadence.charAt(0).toUpperCase() + cadence.slice(1).toLowerCase();
	}
};

export class ReviewCycleTransformer {
	static fromApiData(apiData: ReviewCycleApiData): ReviewCycle {
		let nextReviewDate: Date | null = null;
		if (apiData.next_review_date) {
			const parsedDate = new Date(apiData.next_review_date);
			nextReviewDate = isNaN(parsedDate.getTime()) ? null : parsedDate;
		}

		return {
			id: apiData.id,
			companyId: apiData.company_id,
			notificationCadence: apiData.notification_cadence,
			nextReviewDate,
			active: apiData.active,
			createdAt: apiData.created_at,
			updatedAt: apiData.updated_at,
		};
	}

	static toApiData(reviewCycle: Partial<ReviewCycle>): Partial<ReviewCycleApiData> {
		const apiData: Partial<ReviewCycleApiData> = {};

		if (reviewCycle.notificationCadence !== undefined) {
			apiData.notification_cadence = reviewCycle.notificationCadence;
		}

		if (reviewCycle.nextReviewDate !== undefined) {
			apiData.next_review_date = reviewCycle.nextReviewDate?.toISOString() || null;
		}

		if (reviewCycle.active !== undefined) {
			apiData.active = reviewCycle.active;
		}

		return apiData;
	}
}

/**
 * Result of calculating the last completed review period
 */
export interface LastCompletedPeriod {
	startDate: Date;
	endDate: Date;
	label: string;
}

/**
 * Calculate the last completed period based on the cadence.
 * This is useful for defaulting recap generation to the most recently finished period.
 *
 * For biannual: H1 = Jan 1 - Jun 30, H2 = Jul 1 - Dec 31
 * For quarterly: Previous quarter
 * For annual: Previous year
 *
 * @param cadence - The review cadence (quarterly, biannual, annual)
 * @param referenceDate - Optional date to calculate from (defaults to current date)
 * @returns The start date, end date, and display label for the last completed period
 */
export function getLastCompletedPeriod(cadence: string, referenceDate: Date = new Date()): LastCompletedPeriod {
	const currentYear = referenceDate.getFullYear();
	const currentMonth = referenceDate.getMonth(); // 0-indexed: 0 = January, 11 = December

	const normalizedCadence = cadence.toLowerCase();

	if (normalizedCadence === 'biannual') {
		if (currentMonth < 6) {
			// We're in H1 (Jan-Jun), so last completed is H2 of previous year
			return {
				startDate: new Date(currentYear - 1, 6, 1), // Jul 1
				endDate: new Date(currentYear - 1, 11, 31), // Dec 31
				label: `H2 ${currentYear - 1}`,
			};
		} else {
			// We're in H2 (Jul-Dec), so last completed is H1 of current year
			return {
				startDate: new Date(currentYear, 0, 1), // Jan 1
				endDate: new Date(currentYear, 5, 30), // Jun 30
				label: `H1 ${currentYear}`,
			};
		}
	} else if (normalizedCadence === 'quarterly') {
		// Get the previous quarter
		const previousQuarterEnd = new Date(startOfQuarter(referenceDate).getTime() - 1);
		const previousQuarterStart = startOfQuarter(previousQuarterEnd);
		const quarter = Math.ceil((previousQuarterEnd.getMonth() + 1) / 3);
		return {
			startDate: previousQuarterStart,
			endDate: endOfQuarter(previousQuarterEnd),
			label: `Q${quarter} ${previousQuarterEnd.getFullYear()}`,
		};
	} else if (normalizedCadence === 'annual') {
		// Previous year
		return {
			startDate: new Date(currentYear - 1, 0, 1), // Jan 1
			endDate: new Date(currentYear - 1, 11, 31), // Dec 31
			label: `${currentYear - 1} Annual Review`,
		};
	}

	// Default fallback to previous quarter
	const previousQuarterEnd = new Date(startOfQuarter(referenceDate).getTime() - 1);
	const previousQuarterStart = startOfQuarter(previousQuarterEnd);
	const quarter = Math.ceil((previousQuarterEnd.getMonth() + 1) / 3);
	return {
		startDate: previousQuarterStart,
		endDate: endOfQuarter(previousQuarterEnd),
		label: `Q${quarter} ${previousQuarterEnd.getFullYear()}`,
	};
}
